/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { useContext, type ReactNode } from 'react';
import type { SnippetsSectionProps } from '../Snippets/types';
import { Code } from './Code';
import { getCodeAngularHtml } from '../../../utils/getCodeAngularHtml/getCodeAngularHtml';
import { getCodeHtml } from '../../../utils/getCodeHtml/getCodeHtml';
import { getFilename } from '../../../utils/getFilename/getFilename';
import { HighlightCode } from '../../HighlightCode/HighlightCode';
import { SkeletonLoader } from '../SkeletonLoader/SkeletonLoader';
import { SnippetsSection } from '../Snippets/SnippetsSection';
import { Text } from './Text';
import { SECTION_NAME_ANGULAR_TEMPLATE, SECTION_NAME_HTML_FILE } from '../../../constants/snippetsSectionNames';
import { EditorSettingsContext } from '../../../contexts/EditorSettingsContext';
import { InstallationSettingsContext } from '../../../contexts/InstallationSettingsContext';

export function Html( {
	isLoading,
	number,
	onCopyEvent
}: SnippetsSectionProps ): ReactNode {
	const { selectedTechnology, selectedInstallMethod } = useContext( InstallationSettingsContext );
	const { editorPlugins, editorType, editorLanguage, toolbarOptions } = useContext( EditorSettingsContext );
	const editorOptions = { editorPlugins, editorType, editorLanguage, toolbarOptions };
	const getHtmlCodeFn = selectedTechnology === 'angular' ? getCodeAngularHtml : getCodeHtml;
	const sectionTitle = selectedTechnology === 'angular' ? SECTION_NAME_ANGULAR_TEMPLATE : SECTION_NAME_HTML_FILE;
	const htmlFilename = getFilename( { type: 'html', selectedTechnology } );

	return (
		<SkeletonLoader number={ number } sectionTitle={ sectionTitle } isLoading={ isLoading }>
			<SnippetsSection headerCharacter={ number } title={ sectionTitle }>
				<Text>
					Create or edit an <Code>{ htmlFilename }</Code> file in your project and paste the code from below.
				</Text>
				<HighlightCode onCopyEvent={ onCopyEvent } lang={ 'html' } id={ 'snippet-html' }>
					{ getHtmlCodeFn( { editorOptions, selectedInstallMethod, selectedTechnology } ) }
				</HighlightCode>
			</SnippetsSection>
		</SkeletonLoader>
	);
}
